import React, { useEffect, useRef, useState } from 'react';
import { apiGet, apiPost } from '../../lib/api';
import { Search, Plus, User, Check } from 'lucide-react';
import { toast } from 'sonner';
import { isValidEmail } from '../../lib/validators';

/**
 * Contact picker that mirrors CompanyPicker.
 *
 * Behaviour:
 *  - When `companyId` is given, the picker only loads contacts of that company AND inline-creates
 *    new contacts pre-linked to that company.
 *  - Otherwise it lists all contacts and inline-creates standalone contacts.
 *
 * Props:
 *   value         contact_id currently selected
 *   companyId     restrict suggestions to a single company (optional)
 *   onChange      (contact_id, contact_obj | null) => void
 *   placeholder
 */
export default function ContactPicker({ value, companyId, onChange, onAddNew, refreshSignal, placeholder = 'Search or add a contact…' }) {
  const [contacts, setContacts] = useState([]);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [creating, setCreating] = useState(false);
  // Tendering_30Sep2026 §1: when the full list is 403, fall back to the
  // any-authenticated-user lookup endpoint — limit capped at 100 (its max)
  // and the typed text sent server-side as `q` instead of one static page.
  const [lookupMode, setLookupMode] = useState(false);
  const wrapperRef = useRef(null);

  const reload = () => {
    const url = companyId ? `/crm/contacts?company_id=${companyId}` : '/crm/contacts';
    apiGet(url)
      .then((r) => { setLookupMode(false); setContacts(r.contacts || []); })
      .catch((err) => {
        if (err?.response?.status === 403) {
          setLookupMode(true);
          apiGet('/crm/contacts/lookup?limit=100')
            .then((r) => setContacts(r.items || []))
            .catch(() => {});
        }
      });
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [companyId, refreshSignal]);

  // Server-side search while in lookup mode — re-queries on every keystroke
  // (debounced) instead of filtering a single fixed page client-side.
  useEffect(() => {
    if (!lookupMode) return;
    const handle = setTimeout(() => {
      const q = query.trim();
      apiGet(`/crm/contacts/lookup?limit=100${q ? `&q=${encodeURIComponent(q)}` : ''}`)
        .then((r) => setContacts(r.items || []))
        .catch(() => {});
    }, 300);
    return () => clearTimeout(handle);
  }, [query, lookupMode]);

  useEffect(() => {
    if (value && contacts.length) {
      const c = contacts.find((x) => x.id === value);
      if (c) setSelected(c);
    } else if (!value) {
      setSelected(null);
    }
  }, [value, contacts]);

  useEffect(() => {
    const onClick = (e) => { if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const q = query.trim().toLowerCase();
  // In lookup mode the server already filtered by `q` (name/company) — filtering
  // again client-side on name/email/designation would wrongly drop company-only matches.
  const filtered = lookupMode || !q ? contacts : contacts.filter((c) =>
    (c.name || '').toLowerCase().includes(q) ||
    (c.email || '').toLowerCase().includes(q) ||
    (c.designation || '').toLowerCase().includes(q)
  );

  const handlePick = (c) => {
    setSelected(c);
    setQuery('');
    setOpen(false);
    onChange?.(c.id, c);
  };

  const handleClear = () => { setSelected(null); onChange?.('', null); };

  const handleCreate = async () => {
    const name = query.trim();
    if (!name) return;
    // If the query looks like an email, use it; otherwise create without email
    // (backend requires email — direct user to full form if no email in query)
    const emailFromQuery = isValidEmail(name) ? name : '';
    if (!emailFromQuery && !companyId) {
      toast.error('Please use the full contact form to add a new contact with a valid email address.');
      return;
    }
    setCreating(true);
    try {
      const res = await apiPost('/crm/contacts', { name, email: emailFromQuery, phone: '', company_id: companyId || '', tags: [] });
      const newContact = res.contact || res;
      setContacts((prev) => [...prev, newContact]);
      handlePick(newContact);
      toast.success(`Contact "${name}" created`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail?.existing_contact) {
        // Existing contact found — pick it directly instead of blocking
        const existing = detail.existing_contact;
        setContacts((prev) => prev.some(c => c.id === existing.id) ? prev : [...prev, existing]);
        handlePick(existing);
        toast.info(`Using existing contact: ${existing.name}`);
      } else {
        toast.error(typeof detail === 'string' ? detail : (e?.detail || 'Could not create contact'));
      }
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="relative" ref={wrapperRef}>
      {selected ? (
        <div className="flex items-center justify-between border border-slate-300 rounded-sm px-3 py-2 bg-white" data-testid="contact-picker-selected">
          <div className="flex items-center gap-2 min-w-0">
            <User size={14} className="text-[#3b4872] shrink-0" />
            <div className="min-w-0">
              <div className="text-sm font-semibold truncate">{selected.name}</div>
              <div className="text-[11px] text-slate-500 truncate">{selected.designation || selected.email || '—'}</div>
            </div>
          </div>
          <button type="button" onClick={handleClear} className="text-xs text-slate-500 hover:text-red-600 font-display font-bold uppercase tracking-wider">Change</button>
        </div>
      ) : (
        <div className="border border-slate-300 rounded-sm px-2.5 py-1.5 flex items-center gap-1.5 focus-within:border-[#3b4872] bg-white">
          <Search size={13} className="text-slate-400 shrink-0" />
          <input
            type="text"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            placeholder={placeholder}
            className="flex-1 text-sm border-0 focus:outline-none bg-transparent"
            data-testid="contact-picker-input"
          />
        </div>
      )}
      {open && !selected && (
        <div className="absolute left-0 right-0 mt-1 bg-white border border-slate-200 rounded-sm shadow-lg z-30 max-h-72 overflow-y-auto" data-testid="contact-picker-dropdown">
          {filtered.length === 0 && (
            <div className="px-3 py-3 text-xs text-slate-500">
              {q ? 'No contacts match.' : (companyId ? 'No contacts for this company yet.' : 'Start typing to search.')}
            </div>
          )}
          {filtered.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => handlePick(c)}
              className="w-full text-left px-3 py-2 text-xs hover:bg-slate-50 flex items-center gap-2 border-b border-slate-100 last:border-b-0"
              data-testid={`contact-picker-opt-${c.id}`}
            >
              <User size={12} className="text-slate-400" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate">{c.name}</div>
                <div className="text-[10px] text-slate-500 truncate">{c.designation || c.email || c.company || '—'}</div>
              </div>
              {companyId && c.company_id && c.company_id !== companyId && (
                <span className="text-[10px] text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded shrink-0 max-w-[90px] truncate" title={c.company_name || 'other company'}>
                  {c.company_name || 'other co.'}
                </span>
              )}
              {value === c.id && <Check size={11} className="text-[#3b4872]" />}
            </button>
          ))}
          {q && !filtered.some((c) => (c.name || '').toLowerCase() === q) && !onAddNew && (
            <button
              type="button"
              disabled={creating}
              onClick={handleCreate}
              className="w-full text-left px-3 py-2 text-xs font-semibold text-emerald-700 hover:bg-emerald-50 flex items-center gap-2 border-t border-slate-200"
              data-testid="contact-picker-create-new"
            >
              <Plus size={12} /> {creating ? 'Creating…' : `Add new contact "${query}"`}
            </button>
          )}
          {onAddNew && (
            <button
              type="button"
              onClick={() => { setOpen(false); onAddNew(query); }}
              className="w-full text-left px-3 py-2 text-xs font-semibold text-emerald-700 hover:bg-emerald-50 flex items-center gap-2 border-t border-slate-200"
              data-testid="contact-picker-open-drawer"
            >
              <Plus size={12} /> Add new contact{q ? ` "${query}"` : ''}…
            </button>
          )}
        </div>
      )}
    </div>
  );
}
